import React, { useState, useMemo } from "react";

export interface HeadCell<T extends object> {
  id: string | (keyof T & string);
  label: string | React.ReactNode;
  sortable?: boolean;
  align?: "left" | "center" | "right";
  colSpan?: number;
  sortBy?: (value: T) => any; // returns something sortable
  compareBy?: (a: T, b: T, orderBy: keyof T | string) => number; // compares and returns a number
  tooltip?: string;
  width?: string;
  minWidth?: string;
  withTooltipIcon?: boolean;
  enabled?: boolean;
}

export type Order = "asc" | "desc";

const defaultSortBy = <T extends object>(
  value: T,
  orderBy: keyof T | string
): any => {
  if (!(orderBy in value)) {
    throw new Error(
      `error sorting by ${String(
        orderBy
      )} since it's not a property of ${value}`
    );
  }

  const propertyVal = value[orderBy as keyof T];

  let val: any = propertyVal;

  // special check to see if the propertyVal has a value
  // property on it. If so then use that.
  const objectWithValueProp = propertyVal as unknown as { value: any };
  if (
    objectWithValueProp != null &&
    typeof objectWithValueProp === "object" &&
    "value" in objectWithValueProp
  ) {
    val = val.value;
  }

  // reasonable default that string compares should
  // be case-insensitive
  if (typeof val === "string") {
    val = val.toLowerCase();
  }

  return val;
};

const orderByComparator = <T extends object>(
  a: T,
  b: T,
  orderBy: keyof T | string,
  sortBy?: (val: T, orderBy: keyof T | string) => any
): number => {
  const sortByFunc = sortBy ?? defaultSortBy;
  const aVal = sortByFunc(a, orderBy);
  const bVal = sortByFunc(b, orderBy);

  if (bVal == null && aVal == null) {
    return 0;
  }
  if (bVal == null || bVal < aVal) {
    return 1;
  }
  if (aVal == null || bVal > aVal) {
    return -1;
  }
  return 0;
};

const getComparator = <T extends object>(
  order: Order,
  orderBy: keyof T | string,
  headCell: HeadCell<T>
): ((a: T, b: T) => number) => {
  if (headCell.compareBy) {
    const compareBy = headCell.compareBy;
    return order === "asc"
      ? (a, b) => compareBy(a, b, orderBy)
      : (a, b) => -compareBy(a, b, orderBy);
  }

  return order === "asc"
    ? (a, b) => orderByComparator(a, b, orderBy, headCell.sortBy)
    : (a, b) => -orderByComparator(a, b, orderBy, headCell.sortBy);
};

interface UseDataTableOptions<T extends object> {
  defaultOrderBy?: string;
  defaultOrder?: "desc" | "asc";
  // data that is not subject to sorting
  stickyData?: T[];
}

const useDataTable = <T extends object>(
  data: T[],
  header: HeadCell<T>[],
  options?: UseDataTableOptions<T>
) => {
  const [order, setOrder] = useState<Order>(options?.defaultOrder ?? "desc");
  const [orderBy, setOrderBy] = useState<string>(
    options?.defaultOrderBy ?? header[0]?.id
  );

  const headCellById = useMemo<{ [key: string]: HeadCell<T> }>(
    () =>
      header.reduce(
        (acc, headCell) => {
          acc[headCell.id] = headCell;
          return acc;
        },
        {} as { [key: string]: HeadCell<T> }
      ),
    [header]
  );

  const handleSort = (event: React.MouseEvent, id: string) => {
    const isAsc = orderBy === id && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(id);
  };

  // handles the case where we get new data and we have
  // an old orderBy in state.
  const dataOrderBy = headCellById[orderBy]
    ? orderBy
    : options?.defaultOrderBy ?? header[0]?.id;

  const sortedData = data
    ? [...data].sort(
        getComparator(order, dataOrderBy, headCellById[orderBy] ?? {})
      )
    : [];

  const sortedDataWithSticky = options?.stickyData
    ? [...options.stickyData, ...sortedData]
    : sortedData;

  return {
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handleSort,
    sortedData: sortedDataWithSticky,
    tableRows: sortedDataWithSticky,
  };
};

export default useDataTable;
